import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldL:"#d4a853", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = { width:"100%",padding:"10px 13px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box" };

// JS Date.getDay() convention: 0=Sunday, 1=Monday, ..., 6=Saturday
const DAYS = [
  { idx: 1, name: "Monday",    short: "Mon" },
  { idx: 2, name: "Tuesday",   short: "Tue" },
  { idx: 3, name: "Wednesday", short: "Wed" },
  { idx: 4, name: "Thursday",  short: "Thu" },
  { idx: 5, name: "Friday",    short: "Fri" },
  { idx: 6, name: "Saturday",  short: "Sat" },
  { idx: 0, name: "Sunday",    short: "Sun" },
];

// Roles excluded from time clock (admins/directors don't punch in)
const EXCLUDED_ROLES = ["admin", "director"];

export default function PayrollSettings({ users, onUpdate }) {
  const [schedules, setSchedules] = useState({});
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSchedules = async () => {
    const { data } = await supabase.from("employee_schedules").select("*");
    const map = {};
    (data || []).forEach(s => {
      if (!map[s.user_id]) map[s.user_id] = {};
      map[s.user_id][s.day_of_week] = s;
    });
    setSchedules(map);
  };

  useEffect(() => {
    loadSchedules();
    const ch = supabase.channel("payroll-settings-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "employee_schedules" }, loadSchedules)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const employees = (users || []).filter(u => !EXCLUDED_ROLES.includes(u.role));

  const openEdit = (user) => {
    setEditing(user);
    const userSched = schedules[user.id] || {};
    const draftSched = {};
    DAYS.forEach(d => {
      const existing = userSched[d.idx];
      draftSched[d.idx] = existing ? {
        is_working: !!existing.is_working,
        start_time: (existing.start_time || "09:00:00").slice(0,5),
        end_time: (existing.end_time || "17:00:00").slice(0,5),
      } : {
        is_working: false,
        start_time: "09:00",
        end_time: "17:00",
      };
    });
    setDraft({
      hourly_rate: user.hourly_rate || 0,
      schedule: draftSched,
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const save = async () => {
    if (!editing || !draft) return;
    setSaving(true);

    try {
      // Save hourly rate on profile
      const rate = parseFloat(draft.hourly_rate);
      const { error: rateErr } = await supabase
        .from("profiles")
        .update({ hourly_rate: isNaN(rate) ? 0 : rate })
        .eq("id", editing.id);
      if (rateErr) throw rateErr;

      // Save schedule (upsert per day)
      const existingByDay = schedules[editing.id] || {};
      for (const d of DAYS) {
        const day = draft.schedule[d.idx];
        const payload = {
          user_id: editing.id,
          day_of_week: d.idx,
          start_time: day.is_working ? day.start_time : null,
          end_time: day.is_working ? day.end_time : null,
          is_working: !!day.is_working,
          updated_at: new Date().toISOString(),
        };
        const existing = existingByDay[d.idx];
        if (existing) {
          await supabase.from("employee_schedules").update(payload).eq("id", existing.id);
        } else {
          await supabase.from("employee_schedules").insert([payload]);
        }
      }

      await loadSchedules();
      if (onUpdate) onUpdate();
      closeEdit();
    } catch (e) {
      window.alert("Save failed: " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const updateDay = (dayIdx, field, value) => {
    setDraft(d => ({
      ...d,
      schedule: {
        ...d.schedule,
        [dayIdx]: { ...d.schedule[dayIdx], [field]: value }
      }
    }));
  };

  const formatSchedulePreview = (userId) => {
    const us = schedules[userId];
    if (!us) return "Schedule not set";
    const workingDays = DAYS.filter(d => us[d.idx]?.is_working);
    if (workingDays.length === 0) return "No days scheduled";
    return workingDays.map(d => {
      const s = us[d.idx];
      return `${d.short} ${(s.start_time || "").slice(0,5)}-${(s.end_time || "").slice(0,5)}`;
    }).join(" Â· ");
  };

  const totalWeeklyHours = (userId) => {
    const us = schedules[userId];
    if (!us) return 0;
    let total = 0;
    for (const d of DAYS) {
      const s = us[d.idx];
      if (!s?.is_working || !s.start_time || !s.end_time) continue;
      const [sh, sm] = s.start_time.split(":").map(Number);
      const [eh, em] = s.end_time.split(":").map(Number);
      const minutes = (eh * 60 + em) - (sh * 60 + sm);
      if (minutes > 0) total += minutes / 60;
    }
    return total;
  };

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
      <div style={{ padding:"12px 16px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:10,fontSize:12,color:C.gold }}>
        ðŸ’° Set hourly rate and weekly schedule for each employee. Used by the time clock for check-in/out and by Payroll for biweekly calculations.
      </div>

      {employees.length === 0 ? (
        <div style={{ padding:30,textAlign:"center",color:C.muted,fontSize:13,background:C.light,borderRadius:10 }}>
          No employees yet. Create users in the "Users" tab first.
          <br/><span style={{ fontSize:11 }}>Admins and directors are excluded from the time clock.</span>
        </div>
      ) : employees.map(u => {
        const weekly = totalWeeklyHours(u.id);
        const rate = parseFloat(u.hourly_rate || 0);
        const weeklyEarnings = (weekly * rate).toFixed(2);
        return (
          <div key={u.id} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap" }}>
            <div style={{ width:42,height:42,borderRadius:10,background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.gold,flexShrink:0 }}>
              {(u.full_name || u.email || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{ flex:1,minWidth:200 }}>
              <div style={{ fontSize:14,fontWeight:700,color:C.text }}>{u.full_name || u.email}</div>
              <div style={{ fontSize:11,color:C.muted,marginTop:2 }}>{u.email} Â· <span style={{ textTransform:"capitalize" }}>{u.role}</span></div>
              <div style={{ fontSize:11,color:C.muted,marginTop:5 }}>ðŸ“… {formatSchedulePreview(u.id)}</div>
            </div>
            <div style={{ textAlign:"right",flexShrink:0,minWidth:140 }}>
              <div style={{ fontSize:18,fontWeight:800,color:C.gold,fontFamily:S }}>
                ${rate.toFixed(2)}<span style={{ fontSize:11,fontWeight:400,color:C.muted }}>/hr</span>
              </div>
              {weekly > 0 && (
                <div style={{ fontSize:10,color:C.muted,marginTop:2 }}>
                  ~{weekly.toFixed(1)} hrs/wk Â· ${weeklyEarnings}
                </div>
              )}
              <button onClick={() => openEdit(u)} style={{ marginTop:6,padding:"6px 14px",fontSize:12,fontWeight:600,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontFamily:F }}>
                âœï¸ Edit
              </button>
            </div>
          </div>
        );
      })}

      {editing && draft && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
          <div style={{ background:C.surface,borderRadius:16,width:"100%",maxWidth:580,maxHeight:"92vh",overflow:"auto",padding:24 }}>
            <div style={{ fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4 }}>Pay & Schedule</div>
            <div style={{ fontSize:12,color:C.muted,marginBottom:20 }}>
              {editing.full_name || editing.email} Â· <span style={{ textTransform:"capitalize" }}>{editing.role}</span>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Hourly Rate ($)</label>
              <input type="number" step="0.01" min="0" value={draft.hourly_rate} onChange={e => setDraft({ ...draft,hourly_rate:e.target.value })} style={inp} placeholder="e.g. 25.00" />
            </div>

            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8 }}>
              <div style={{ fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em" }}>Weekly Schedule</div>
              <div style={{ fontSize:11,color:C.muted }}>Toggle days, set start/end times</div>
            </div>

            <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:20 }}>
              {DAYS.map(d => {
                const day = draft.schedule[d.idx];
                return (
                  <div key={d.idx} style={{ display:"grid",gridTemplateColumns:"110px 1fr 14px 1fr",gap:8,alignItems:"center",padding:"8px 12px",background:day.is_working?C.goldBg:C.light,border:`1px solid ${day.is_working?C.borderGold:C.border}`,borderRadius:8 }}>
                    <label style={{ display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,fontWeight:600,color:day.is_working?C.text:C.muted }}>
                      <input type="checkbox" checked={day.is_working} onChange={e => updateDay(d.idx,"is_working",e.target.checked)} style={{ accentColor:C.gold,cursor:"pointer",width:16,height:16 }} />
                      {d.name}
                    </label>
                    <input type="time" value={day.start_time} onChange={e => updateDay(d.idx,"start_time",e.target.value)} disabled={!day.is_working} style={{ ...inp,opacity:day.is_working?1:0.4,padding:"7px 10px" }} />
                    <span style={{ textAlign:"center",color:C.muted,fontSize:14 }}>â†’</span>
                    <input type="time" value={day.end_time} onChange={e => updateDay(d.idx,"end_time",e.target.value)} disabled={!day.is_working} style={{ ...inp,opacity:day.is_working?1:0.4,padding:"7px 10px" }} />
                  </div>
                );
              })}
            </div>

            <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
              <button onClick={closeEdit} disabled={saving} style={{ padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:saving?"not-allowed":"pointer",fontFamily:F }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1 }}>{saving?"Saving...":"ðŸ’¾ Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
